import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Accessibility as AccessibilityIcon,
  Healing as HealingIcon,
  Elderly as ElderlyIcon,
  Home as HomeIcon,
  Psychology as PsychologyIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface ServiceCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  path: string;
}

export const ServiceCategoriesSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const serviceCategories: ServiceCategory[] = [
    {
      id: 'physical-therapy',
      title: 'Physical Therapy',
      description: 'Recover from injuries and improve mobility with licensed physical therapists.',
      icon: <HealingIcon sx={{ fontSize: '2.5rem' }} />,
      color: theme.palette.primary.main,
      path: '/search?service=physical-therapy',
    },
    {
      id: 'occupational-therapy',
      title: 'Occupational Therapy',
      description: 'Regain independence in daily activities with expert occupational therapists.',
      icon: <AccessibilityIcon sx={{ fontSize: '2.5rem' }} />,
      color: theme.palette.secondary.main,
      path: '/search?service=occupational-therapy',
    },
    {
      id: 'senior-care',
      title: 'Senior Care',
      description: 'Compassionate care for elderly patients with specialized senior care providers.',
      icon: <ElderlyIcon sx={{ fontSize: '2.5rem' }} />,
      color: theme.palette.success.main,
      path: '/search?service=senior-care',
    },
    {
      id: 'home-health',
      title: 'Home Health',
      description: 'Professional medical care delivered in the comfort of your own home.',
      icon: <HomeIcon sx={{ fontSize: '2.5rem' }} />,
      color: theme.palette.info.main,
      path: '/search?service=home-health',
    },
    {
      id: 'mental-health',
      title: 'Mental Health',
      description: 'Support for mental wellness with licensed therapists and counselors.',
      icon: <PsychologyIcon sx={{ fontSize: '2.5rem' }} />,
      color: theme.palette.warning.main,
      path: '/search?service=mental-health',
    },
    {
      id: 'nursing-care',
      title: 'Nursing Care',
      description: 'Skilled nursing services for complex medical needs and recovery.',
      icon: <HospitalIcon sx={{ fontSize: '2.5rem' }} />,
      color: theme.palette.error.main,
      path: '/search?service=nursing-care',
    },
  ];

  const handleCategoryClick = (path: string) => {
    navigate(path);
  };

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: theme.palette.grey[50] }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 'bold',
              color: theme.palette.text.primary,
              mb: 2,
            }}
          >
            Our Care Services
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Professional, compassionate care across a wide range of specialties. 
            Find the right caregiver for your specific needs.
          </Typography>
        </Box>

        {/* Service Categories Grid */}
        <Grid container spacing={3}>
          {serviceCategories.map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category.id}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    elevation: 8,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleCategoryClick(category.path)}
                  sx={{ height: '100%', p: 0 }}
                >
                  <CardContent
                    sx={{
                      p: 4,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: `${category.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: category.color,
                      }}
                    >
                      {category.icon}
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 'bold',
                        color: theme.palette.text.primary,
                        mb: 2,
                      }}
                    >
                      {category.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                        flexGrow: 1,
                      }}
                    >
                      {category.description}
                    </Typography>

                    {/* Learn More Link */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: category.color,
                        fontWeight: 'bold',
                        mt: 2,
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Learn More →
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              mb: 3,
            }}
          >
            Don't see what you're looking for?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 'bold',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={() => navigate('/search')}
          >
            Browse all caregivers →
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
